import React, { useRef } from "react";
import styled, { css } from "styled-components";
import { Button } from "@vitalsource/vstui";
import { FormattedMessage, useIntl } from "react-intl";
import useCookieAccept from "../../../../common/providers/useCookieAccept";
import useUser from "../../framework/connectors/user/useUser";
import CookieDialog from "./CookieDialog";
import useClickToOpen from "../../../../common/ui/useClickToOpen";
import { useBrand } from "../../../../common/providers/brand/brand-context";
import { generateUUID } from "../../../../common/utils/uuid";
import ReactDOM from "react-dom";

const BANNER_SIDE_MARGIN_PCT = 7;

const smallBreakpoint = (styles) => css`
  @media (max-width: 760px) {
    ${styles}
  }
`;

const Styled = {
  Container: styled.div`
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
    padding: 24px 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: fixed;
    z-index: 1000;
    bottom: 16px;
    width: ${100 - BANNER_SIDE_MARGIN_PCT * 2}%;
    max-width: calc(1440px - ${BANNER_SIDE_MARGIN_PCT * 2}%);
    left: 50%;
    transform: translateX(-50%);
    font-size: 1.6rem;

    ${smallBreakpoint`
      padding: 16px 22px;
      width: 100%;
      bottom: 0;
      left: 0;
      transform: translateX(0);
      border-radius: 0;
      box-shadow: 0 -4px 16px 0 rgba(0, 0, 0, 0.36);
      flex-direction: column;
    `}
  `,
  ButtonContainer: styled.div`
    display: flex;

    ${smallBreakpoint`
       padding-top: 20px;
    `}
  `,
  DisableButton: styled(Button)`
    margin: 0 37px;
  `,
  DisableText: styled.span`
    white-space: nowrap;
  `,
};

export default function CookieBanner() {
  const { userInfo } = useUser();
  const { areCookiesAccepted, accept, deny } = useCookieAccept(userInfo);
  const { buttonProps, contentProps } = useClickToOpen();
  const notificationRequired = useBrand().getProp("Cookies.notify");
  const intl = useIntl();
  const ariaRefId = useRef(generateUUID());

  if (!notificationRequired || areCookiesAccepted) {
    return null;
  }

  const content = (
    <div
      role="region"
      aria-label={intl.formatMessage({ id: "cookies.banner" })}
    >
      <Styled.Container
        role="dialog"
        aria-live="polite"
        aria-label={intl.formatMessage({ id: "cookies.policy" })}
        aria-describedby={ariaRefId.current}
      >
        <span id={ariaRefId.current}>
          <FormattedMessage id="cookies.alert" />
        </span>
        <Styled.ButtonContainer>
          <Styled.DisableButton inline {...buttonProps}>
            <Styled.DisableText>
              <FormattedMessage id="cookies.disable" />
            </Styled.DisableText>
          </Styled.DisableButton>
          <Button onClick={accept}>
            <FormattedMessage id="base.accept" />
          </Button>
        </Styled.ButtonContainer>
        <CookieDialog {...contentProps} denyCookies={deny} />
      </Styled.Container>
    </div>
  );

  // a11y, always put at end of dom
  return ReactDOM.createPortal(content, document.body);
}
